import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { logPageView } from './utils/analytics';

// Importa las páginas de cada rol
import Login from './components/Login';
import ResetPassword from './pages/ResetPassword';
import LandingPage from './pages/LandingPage';
import AvisoPrivacidad from './pages/AvisoPrivacidad';
import ModalSeleccionFraccionamiento from './components/ModalSeleccionFraccionamiento';
import ModalSeleccionCasa from './components/ModalSeleccionCasa';

// Importa los componentes que contendrán las rutas de cada rol
import PlatformAdminRoutes from './routes/PlatformAdminRoutes';
import SuperAdminRoutes from './routes/SuperAdminRoutes';
import UserRoutes from './routes/UserRoutes';
import CasetaRoutes from './routes/CasetaRoutes';
import OfflineStatus from './components/OfflineStatus';
import UpdateModal from './components/UpdateModal';
import ModalAvisoPrivacidadObligatorio from './components/ModalAvisoPrivacidadObligatorio';
import { useVersionChecker } from './hooks/useVersionChecker';

const AppRouter = () => {

    const { userData: user, loading: authLoading, cambiarFraccionamiento, misCasas, cambiarCasaActiva } = useAuth();
    const location = useLocation();
    const { needsUpdate, latestVersion, playStoreUrl, message } = useVersionChecker();
    const [showSelectionModal, setShowSelectionModal] = useState(false);
    const [showCasaModal, setShowCasaModal] = useState(false);

    const fraccList = useMemo(() => {
        if (!user) return [];
        const list = new Set();
        if (user.fraccionamientoId) list.add(user.fraccionamientoId);
        if (Array.isArray(user.fraccionamientosAsignados)) {
            user.fraccionamientosAsignados.forEach(id => {
                if (typeof id === 'string') list.add(id);
                else if (id && id.id) list.add(id.id);
            });
        }
        return Array.from(list);
    }, [user]);

    useEffect(() => {
        if (user && fraccList.length > 1) {
            const isSessionSelected = sessionStorage.getItem('session_fraccionamiento_selected') === 'true';
            const storedActive = localStorage.getItem('active_fraccionamiento_id');
            if (!isSessionSelected || !storedActive) {
                setShowSelectionModal(true);
            } else {
                setShowSelectionModal(false);
            }
        } else {
            setShowSelectionModal(false);
        }
    }, [user, fraccList]);

    useEffect(() => {
        if (user && Array.isArray(misCasas) && misCasas.length > 1 && !showSelectionModal) {
            const isCasaSessionSelected = sessionStorage.getItem('session_casa_selected') === 'true';
            const storedCasaActive = localStorage.getItem('active_casa_id');
            if (!isCasaSessionSelected || !storedCasaActive) {
                setShowCasaModal(true);
            } else {
                setShowCasaModal(false);
            }
        } else {
            setShowCasaModal(false);
        }
    }, [user, misCasas, showSelectionModal]);

    // Rastrear cambios de página en analítica
    useEffect(() => {
        logPageView(location.pathname);
    }, [location.pathname]);

    // Detectar si es móvil
    const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // 1. Mientras se verifica la autenticación, muestra un loader.
    if (authLoading) {
        return <p>Verificando sesión...</p>;
    }

    // 2. Si no hay usuario, permitimos rutas públicas (landing, login, registro, reset-password)
    if (!user) {
        return (
            <>
                <OfflineStatus />
                <UpdateModal 
                    isOpen={needsUpdate} 
                    latestVersion={latestVersion} 
                    playStoreUrl={playStoreUrl} 
                    message={message} 
                />
                <Routes>
                    {/* En móvil, redirigir directamente al login en lugar de mostrar landing */}
                    <Route path="/" element={isMobile ? <Navigate to="/login" replace /> : <LandingPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/aviso-privacidad" element={<AvisoPrivacidad />} />
                    <Route path="*" element={<Navigate to={isMobile ? "/login" : "/"} />} />
                </Routes>
            </>
        );

    }

    // 3. Si hay un usuario, determina qué conjunto de rutas mostrar según su rol.
    // Normalize roles so values like 'residente' or 'user' both work for the user routes.
    const role = String(user.rol || '').toLowerCase();

    console.log('AppRouter: Renderizando con userData:', user);
    console.log('AppRouter: Rol detectado:', role);
    console.log('AppRouter: Rol raw:', user.rol);

    return (
        <>
            <OfflineStatus />
            <UpdateModal 
                isOpen={needsUpdate} 
                latestVersion={latestVersion} 
                playStoreUrl={playStoreUrl} 
                message={message} 
            />
            <ModalSeleccionFraccionamiento
                isOpen={showSelectionModal}
                fraccionamientoIds={fraccList}
                userRol={role}
                onSelectFraccionamiento={(selectedId) => {
                    cambiarFraccionamiento(selectedId);
                    setShowSelectionModal(false);
                }}
            />
            <ModalSeleccionCasa
                isOpen={showCasaModal}
                casas={misCasas}
                onSelectCasa={(casaId) => {
                    cambiarCasaActiva(casaId);
                    setShowCasaModal(false);
                }}
            />
            <ModalAvisoPrivacidadObligatorio />
            <Routes>
                {role === 'platformadmin' && <Route path="/*" element={<PlatformAdminRoutes />} />}
                {role === 'superadmin' && <Route path="/*" element={<SuperAdminRoutes />} />}
                {role === 'caseta' && <Route path="/*" element={<CasetaRoutes />} />}
                {(role === 'user' || role === 'residente') && <Route path="/*" element={<UserRoutes />} />}

                {/* Fallback: si userData existe pero rol está vacío, mostrar CasetaRoutes como default */}
                {!role && user && <Route path="/*" element={<CasetaRoutes />} />}

                {/* Si no hay coincidencia de rol ni userData, ir a login */}
                <Route path="/login" element={<Navigate to="/" />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </>
    );

};

export default AppRouter;